<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDetectKeyboardLayout } from 'dashboard/composables/useDetectKeyboardLayout';
import { SHORTCUT_KEYS, KEYS } from './constants';
import {
  LAYOUT_QWERTZ,
  keysToModifyInQWERTZ,
} from 'shared/helpers/KeyboardHelpers';
import Hotkey from 'dashboard/components/base/Hotkey.vue';

defineProps({ show: Boolean });
defineEmits(['close']);

const { t } = useI18n();
const currentLayout = ref(null);

const title = computed(
  () => item => t(`KEYBOARD_SHORTCUTS.TITLE.${item.label}`)
);

// Added this function to check if the keySet needs a shift key
// This is used to display the shift key in the modal
// If the current layout is QWERTZ and the keySet contains a key that needs a shift key
// If layout is QWERTZ then we add the Shift+keysToModify to fix an known issue
// https://github.com/chatwoot/chatwoot/issues/9492
const needsShiftKey = computed(
  () => keySet =>
    currentLayout.value === LAYOUT_QWERTZ &&
    keySet.some(key => keysToModifyInQWERTZ.has(key))
);

onMounted(async () => {
  currentLayout.value = await useDetectKeyboardLayout();
});
</script>

<template>
  <woot-modal
    :show="show"
    size="modal-key-shortcuts"
    :on-close="() => $emit('close')"
  >
    <div class="shortcut-modal">
      <header class="shortcut-modal__header">
        <span class="shortcut-modal__header-icon">
          <span class="i-lucide-keyboard size-5" />
        </span>
        <div class="min-w-0">
          <h2 class="truncate text-lg font-semibold text-n-slate-12">
            {{ $t('SIDEBAR_ITEMS.KEYBOARD_SHORTCUTS') }}
          </h2>
        </div>
      </header>

      <div class="shortcut-modal__body">
        <section class="shortcut-modal__featured">
          <span
            class="grid size-9 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
          >
            <span class="i-lucide-command size-4" />
          </span>
          <span class="min-w-0 flex-1 text-sm font-medium text-n-slate-12">
            {{ $t('KEYBOARD_SHORTCUTS.TOGGLE_MODAL') }}
          </span>
          <div class="shortcut-keys">
            <Hotkey custom-class="key key--wide normal-case">
              {{ KEYS.WIN }}
            </Hotkey>
            <Hotkey custom-class="key">{{ KEYS.SLASH }}</Hotkey>
          </div>
        </section>

        <div class="shortcut-modal__grid">
          <article
            v-for="shortcut in SHORTCUT_KEYS"
            :key="shortcut.id"
            class="shortcut-card"
          >
            <h3
              class="min-w-0 flex-1 text-[13px] font-medium leading-5 text-n-slate-11"
            >
              {{ title(shortcut) }}
            </h3>
            <div class="shortcut-keys">
              <Hotkey
                v-if="needsShiftKey(shortcut.keySet)"
                custom-class="key normal-case"
              >
                {{ KEYS.SHIFT }}
              </Hotkey>

              <template
                v-for="(key, index) in shortcut.displayKeys"
                :key="index"
              >
                <Hotkey
                  v-if="key !== KEYS.SLASH"
                  custom-class="key normal-case"
                >
                  {{ key }}
                </Hotkey>
                <span v-else class="shortcut-separator">{{ key }}</span>
              </template>
            </div>
          </article>
        </div>
      </div>
    </div>
  </woot-modal>
</template>

<style scoped>
.shortcut-modal {
  @apply flex max-h-[min(46rem,calc(100dvh-2rem))] flex-col overflow-hidden bg-n-solid-1;
}

.shortcut-modal__header {
  @apply flex shrink-0 items-center gap-3 border-b border-n-weak px-5 py-4 pe-14;
}

.shortcut-modal__header-icon {
  @apply grid size-10 shrink-0 place-items-center rounded-xl bg-n-brand text-white shadow-sm shadow-n-brand/25;
}

.shortcut-modal__body {
  @apply min-h-0 overflow-y-auto p-4 sm:p-5;
}

.shortcut-modal__featured {
  @apply mb-4 flex min-h-16 items-center gap-3 rounded-2xl border border-n-brand/20 bg-n-brand/5 px-3.5 py-3;
}

.shortcut-modal__grid {
  @apply grid grid-cols-1 gap-2 md:grid-cols-2;
}

.shortcut-card {
  @apply flex min-h-14 items-center justify-between gap-3 rounded-xl border border-n-weak bg-n-alpha-1 px-3.5 py-2.5 transition hover:border-n-strong hover:bg-n-alpha-2;
}

.shortcut-keys {
  @apply flex shrink-0 items-center gap-1.5;
}

.shortcut-separator {
  @apply px-0.5 text-xs font-semibold text-n-slate-9;
}

.key {
  @apply min-h-7 min-w-7 rounded-lg border border-n-weak bg-n-solid-2 px-2 py-1 font-mono text-[11px] font-semibold text-n-slate-11 shadow-sm;
}

.key--wide {
  @apply min-w-[4.5rem];
}

:global(.modal-mask[data-modal-size='modal-key-shortcuts']) {
  @apply bg-n-alpha-black2 backdrop-blur-md;
}

:global(.modal-container.modal-key-shortcuts) {
  width: min(54rem, calc(100vw - 1.5rem)) !important;
  max-width: 54rem !important;
  max-height: calc(100dvh - 1.5rem);
  @apply overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-2xl;
}
</style>
